// Single source of truth for the brand visual identity.
//
// The site uses a warm terracotta/coral palette that pairs with the hero
// illustration (a coral globe) and the hot-air-balloon logo. Everything is
// keyed off this file — components import semantic tokens and never hardcode
// hex values. A future palette pivot is a one-file change here.
//
//   import { PALETTE, GRADIENTS, BORDERS, SHADOWS, TINTS, alpha } from '../theme';
//
//   sx={{
//     color: PALETTE.text,
//     border: BORDERS.soft,
//     boxShadow: SHADOWS.card,
//     backgroundColor: TINTS.primary,
//   }}

import { createTheme } from '@mui/material/styles';

// ─── Raw RGB triplets ───────────────────────────────────────────────────────
// Kept as comma-separated strings so the alpha helper can splice them into
// rgba() without parsing hex at runtime.

const RGB = {
    text:    '26, 20, 16',
    primary: '232, 99, 45',
    accent:  '90, 125, 97'
};

/**
 * Build an rgba() string from a base RGB key and an opacity in [0, 1].
 *
 * Use this instead of writing `rgba(26, 20, 16, 0.08)` inline — that's how
 * we ended up scattering raw colors across 60 files last time.
 *
 *   alpha('text', 0.08)     // → 'rgba(26, 20, 16, 0.08)'
 *   alpha('primary', 0.4)   // → 'rgba(232, 99, 45, 0.4)'
 */
export const alpha = (base, a) => `rgba(${RGB[base]}, ${a})`;

// ─── Palette ────────────────────────────────────────────────────────────────

export const PALETTE = {
    // Coral / terracotta — the brand color. Used for CTAs, links, accents.
    primary:      '#e8632d',
    primaryDark:  '#b8401a',
    primaryLight: '#fb8a5e',
    primary50:    '#fef0e8',   // very soft background tint
    primary100:   '#fdd9c4',   // soft hover background

    // Sage green — secondary accent, used sparingly.
    accent:       '#5a7d61',
    accentMuted:  '#e6ede0',

    // Warm-neutral text scale (replaces a cool slate/navy scale).
    text:           '#1a1410',   // headings
    textBody:       '#3a342e',   // primary body
    textMuted:      '#6b635c',   // secondary body, captions
    textFaint:      '#a09a93',   // disabled, timestamps
    textVeryFaint:  '#c8c0b8',   // placeholder ghost text

    // Surfaces.
    page:          '#faf6f0',    // page background (warm cream)
    surface:       '#ffffff',    // cards
    surfaceSubtle: '#f3ede4',    // soft inset surface

    // Status (system messages only).
    success: '#4f9a6a',
    danger:  '#c5524a'
};

// ─── Gradients ──────────────────────────────────────────────────────────────

export const GRADIENTS = {
    primary:     `linear-gradient(45deg, ${PALETTE.primaryLight}, ${PALETTE.primary})`,
    primaryDeep: `linear-gradient(45deg, ${PALETTE.primary}, ${PALETTE.primaryDark})`,
    brandMark:   `linear-gradient(135deg, ${PALETTE.primaryLight}, ${PALETTE.primary})`,
    // For "gradient text" (background-clip: text on the headline accent).
    headline:    `linear-gradient(45deg, ${PALETTE.primary}, ${PALETTE.primaryDark})`
};

// ─── Borders ────────────────────────────────────────────────────────────────
// Standard border tokens. Use BORDERS.* in sx={{ border: ... }} for shorthand,
// or BORDER_COLORS.* when you need just `borderColor: ...` for a hover state.

export const BORDER_COLORS = {
    faint:   alpha('text', 0.06),
    soft:    alpha('text', 0.08),
    default: alpha('text', 0.10),
    strong:  alpha('text', 0.18),
    primary: alpha('primary', 0.40)
};

export const BORDERS = {
    faint:    `1px solid ${BORDER_COLORS.faint}`,
    soft:     `1px solid ${BORDER_COLORS.soft}`,
    default:  `1px solid ${BORDER_COLORS.default}`,
    strong:   `1px solid ${BORDER_COLORS.strong}`,
    primary:  `1px solid ${BORDER_COLORS.primary}`
};

// ─── Shadows ────────────────────────────────────────────────────────────────

export const SHADOWS = {
    card:      `0 1px 3px ${alpha('text', 0.04)}, 0 6px 18px ${alpha('text', 0.05)}`,
    cardHover: `0 4px 8px ${alpha('text', 0.06)}, 0 16px 40px ${alpha('primary', 0.18)}`,
    cta:       `0 8px 20px ${alpha('primary', 0.40)}`,
    ctaHover:  `0 12px 28px ${alpha('primary', 0.50)}`,
    brandMark: `0 2px 8px ${alpha('primary', 0.35)}`
};

// ─── Tints ──────────────────────────────────────────────────────────────────
// Soft fills — backgrounds for badges, active nav links, hover states.

export const TINTS = {
    primary:      alpha('primary', 0.10),
    primaryHover: alpha('primary', 0.18),
    primaryGlow:  alpha('primary', 0.06),
    textHover:    alpha('text', 0.04)
};

// ─── Typography ─────────────────────────────────────────────────────────────

export const FONT_FAMILY =
    `"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif`;

// ─── Background pattern ─────────────────────────────────────────────────────
// Applied on <body> globally via index.css. Exported too so any page-level
// section can re-apply it deliberately.

export const BG_PATTERN = {
    backgroundImage:    `radial-gradient(${alpha('primary', 0.12)} 1px, transparent 1px)`,
    backgroundSize:     '22px 22px',
    backgroundPosition: '0 0'
};

// ─── MUI theme ──────────────────────────────────────────────────────────────

export const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main:  PALETTE.primary,
            dark:  PALETTE.primaryDark,
            light: PALETTE.primaryLight,
            contrastText: '#ffffff'
        },
        background: {
            default: PALETTE.page,
            paper:   PALETTE.surface
        },
        text: {
            primary:   PALETTE.text,
            secondary: PALETTE.textBody,
            disabled:  PALETTE.textFaint
        },
        divider: alpha('text', 0.10)
    },
    typography: {
        fontFamily: FONT_FAMILY,
        fontWeightRegular: 400,
        fontWeightMedium:  500,
        fontWeightBold:    700,
        h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 },
        h2: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 },
        h3: { fontWeight: 800, letterSpacing: '-0.01em' },
        button: { textTransform: 'none', fontWeight: 700 }
    }
});
